import { useContext, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { t } from "i18next";
import { HiOutlineX } from "react-icons/hi";
import { HiOutlineCheckCircle, HiOutlineExclamationTriangle, HiOutlineEye, HiOutlinePencilSquare } from "react-icons/hi2";
import { TbCalendarBolt, TbClock, TbPencil, TbShare2 } from "react-icons/tb";
import { Button, Dialog } from "components/ui";
import Segment from "components/ui/Segment";
import { RuleGroupsContext } from "contexts/RuleGroupsContext";
import { SessionContext } from "contexts/SessionContext";
import { formatInUserTz } from "utils/timezone";
import PostArticleButton from "../PostArticleButton";
import PostArticleOptionsButton from "../PostArticleOptionsButton";
import PostTagsButton from "../PostTagsButton";
import ValidationErrors from "../ValidationErrors";
import PublishErrors from "../PublishErrors";
import ComposerNetworkPills from "./ComposerNetworkPills";
import ComposerForm from "./ComposerForm";
import ComposerPreviewPane from "./ComposerPreviewPane";

/**
 * Flag-gated two-pane editor shown instead of the legacy fullscreen
 * mimic-card (see the POST_COMPOSER_ENABLED branch in PostArticle.js).
 * Left: structured form. Right: the network preview, read-only, updating
 * live. All state and handlers live in PostArticle and are passed down —
 * this component is purely presentational plus close/confirm logic.
 */
const PostComposer = ({
    postGroup,
    posts,
    status,
    isEditable,
    activeSocialNetwork,
    setActiveSocialNetwork,
    validationErrors,
    showSaveButton,
    isLoadingPostArticleButton,
    PreviewComponent,
    postIndex,
    forceFullScreen,
    showOptionsButton = true,
    showPostArticleButton = true,
    canDuplicate = false,
    onTextEdit,
    onTitleEdit,
    onLinkEdit,
    onAssetNew,
    onAssetDelete,
    onAssetEdit,
    onCoverEdit,
    onPreferencesEdit,
    onCommentsToCreateEdit,
    onCreateNetworkPost,
    onDeleteNetworkPost,
    onReplaceNetworkPost,
    onDeleteIndexPost,
    onSave,
    onPostNow,
    onDeletePostGroup,
    onDuplicatePostGroup,
    onExportAsHTML,
    onClickTag,
    updateFieldValuePostGroup,
    onClose
}) => {

    const { user } = useContext(SessionContext);
    const { getAutomatedPublishAt } = useContext(RuleGroupsContext);
    const automatedPublishAt = getAutomatedPublishAt(postGroup);

    const [mobilePane, setMobilePane] = useState("edit");
    const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
    // Only edits made in this composer session count as discardable — a
    // freshly opened (even unsaved) post closes without a prompt.
    const [isDirty, setIsDirty] = useState(false);

    const markDirty = (fn) => (...args) => {
        setIsDirty(true);
        return fn?.(...args);
    };

    useEffect(() => {
        if (!showSaveButton) setIsDirty(false);
    }, [showSaveButton]);

    const requestClose = () => {
        if (isDirty && showSaveButton && isEditable) {
            setConfirmDiscardOpen(true);
            return;
        }
        onClose?.();
    };

    // Ref keeps the Escape handler's closure fresh while the listener is
    // attached exactly once for the component's lifetime.
    const requestCloseRef = useRef(requestClose);
    requestCloseRef.current = requestClose;

    // The legacy fullscreen editor never locked body scroll — the composer
    // does, like every other modal surface in the app.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== "Escape" || e.defaultPrevented) return;
            // Typing surfaces use Escape themselves (MagicEditor's AI menu)
            if (e.target?.closest?.('input, textarea, [contenteditable="true"]')) return;
            // A stacked layer (media library Dialog, confirm dialog,
            // fullscreen media viewer) is open and handles its own Escape
            if (document.querySelector('.ReactModal__Overlay')) return;
            if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
            requestCloseRef.current();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const scheduleSummary = postGroup?.draft && status != "published"
        ? { icon: <TbPencil className="text-lg text-gray-500" />, text: t('composer.draftSummary', 'Draft — will not publish automatically') }
        : status == "published"
            ? { icon: <HiOutlineCheckCircle className="text-lg text-emerald-500" />, text: t('composer.publishedSummary', 'Published') }
            : status == "publishing"
                ? { icon: <TbShare2 className="text-lg text-cyan-600 animate-pulse" />, text: t('composer.publishingSummary', 'Publishing...') }
                : postGroup?.publish_at
                    ? { icon: <TbClock className="text-lg text-purple-500" />, text: `${t('composer.scheduledSummary', 'Scheduled')} · ${formatInUserTz(postGroup?.publish_at, user, "MMM D, h:mm A")}` }
                    : automatedPublishAt
                        ? { icon: <TbCalendarBolt className="text-lg text-purple-500" />, text: `${t('composer.autoListedSummary', 'Auto-listed')} · ${formatInUserTz(automatedPublishAt, user, "MMM D, h:mm A")}` }
                        : { icon: <TbClock className="text-lg text-tertiary-400" />, text: t('composer.notScheduledSummary', 'Not scheduled yet') };

    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-0 sm:p-4 lg:p-8"
                onClick={(e) => e.target === e.currentTarget && requestClose()}
                role="dialog"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: 8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    className="relative flex flex-col w-full max-w-7xl h-full sm:h-[min(92vh,900px)] bg-secondary-500 dark:bg-gray-800 rounded-none sm:rounded-2xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden"
                >
                    {/* ───── Header ───── */}
                    <div className="shrink-0 h-14 sm:h-16 px-4 sm:px-6 flex items-center gap-3 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="!text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
                            {postGroup?.id
                                ? t('composer.titleEdit', 'Edit post')
                                : t('composer.titleNew', 'New post')
                            }
                        </h3>
                        <div className="shrink-0 [&_.dropdown]:!w-auto">
                            <PostTagsButton
                                onClickTag={onClickTag}
                                selectedTags={postGroup?.tags}
                                portal={true}
                                buttonClassName="!bg-gray-100 hover:!bg-gray-200 dark:!bg-gray-700 dark:hover:!bg-gray-600 !text-tertiary-600 dark:!text-gray-300 ring-1 ring-gray-200 dark:ring-gray-600"
                            />
                        </div>
                        <div className="flex-1" />
                        <div className="flex items-center gap-2 shrink-0">
                            {isDirty && showSaveButton && (
                                <motion.span
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="hidden sm:flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap shrink-0"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                                    {t('composer.unsavedChanges', 'Unsaved changes')}
                                </motion.span>
                            )}
                            {showOptionsButton && (
                                <div className="shrink-0 [&_.dropdown]:!w-auto">
                                    <PostArticleOptionsButton
                                        status={status}
                                        onDeletePostGroup={onDeletePostGroup}
                                        onToggleFullScreen={onClose}
                                        onDuplicatePostGroup={canDuplicate ? onDuplicatePostGroup : undefined}
                                        onExportAsHTML={onExportAsHTML}
                                        postGroup={postGroup}
                                        forceFullScreen={forceFullScreen}
                                        activeSocialNetwork={activeSocialNetwork}
                                        size="sm"
                                    />
                                </div>
                            )}
                            <Button
                                size="sm"
                                variant="plain"
                                className="!rounded-xl !h-10 !w-10 !p-0 !shrink-0 flex items-center justify-center"
                                icon={<HiOutlineX className="text-2xl" />}
                                onClick={() => requestClose()}
                            />
                        </div>
                    </div>

                    {/* ───── Mobile pane switch (edge-to-edge full-bleed bar) ───── */}
                    <Segment
                        size="sm"
                        className="shrink-0 lg:hidden w-full border-b border-gray-200 dark:border-gray-700"
                        value={[mobilePane]}
                        onChange={(val) => {
                            const next = Array.isArray(val) ? val[0] : val;
                            if (next) setMobilePane(next);
                        }}
                    >
                        <Segment.Item value="edit" type="button" className="flex-1 flex items-center justify-center gap-1.5 !h-11 !rounded-none !border-0">
                            <HiOutlinePencilSquare className="text-lg" />
                            <span>{t('composer.tabEdit', 'Edit')}</span>
                        </Segment.Item>
                        <Segment.Item value="preview" type="button" className="flex-1 flex items-center justify-center gap-1.5 !h-11 !rounded-none !border-0">
                            <HiOutlineEye className="text-lg" />
                            <span>{t('composer.tabPreview', 'Preview')}</span>
                        </Segment.Item>
                    </Segment>

                    {/* ───── Panes ───── */}
                    <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(420px,46%)]">
                        {/* Left: form */}
                        <div className={`flex-col min-h-0 overflow-hidden ${mobilePane === "preview" ? `hidden lg:flex` : `flex`}`}>
                            <div className="shrink-0 px-4 sm:px-6 py-3 border-b border-gray-100 dark:border-gray-700/60">
                                <ComposerNetworkPills
                                    posts={posts}
                                    postGroup={postGroup}
                                    activeSocialNetwork={activeSocialNetwork}
                                    setActiveSocialNetwork={setActiveSocialNetwork}
                                    isEditable={isEditable}
                                    validationErrors={validationErrors}
                                    onCreateNetworkPost={markDirty(onCreateNetworkPost)}
                                    onDeleteNetworkPost={markDirty(onDeleteNetworkPost)}
                                    onReplaceNetworkPost={markDirty(onReplaceNetworkPost)}
                                />
                            </div>
                            <div className="shrink-0 empty:hidden flex flex-col gap-2 px-4 sm:px-6 pt-3 [&>*]:rounded-lg [&>*]:overflow-hidden">
                                <ValidationErrors
                                    activeSocialNetwork={activeSocialNetwork}
                                    validationErrors={validationErrors}
                                />
                                <PublishErrors
                                    activeSocialNetwork={activeSocialNetwork}
                                    postGroup={postGroup}
                                />
                                {!isEditable && (
                                    <div className={`px-3 py-2 text-sm flex items-center gap-2 ${status == "publishing"
                                        ? `bg-cyan-50 text-cyan-800 dark:bg-cyan-500/10 dark:text-cyan-300`
                                        : `bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300`
                                    }`}>
                                        <HiOutlineCheckCircle className={`text-lg shrink-0 ${status == "publishing" ? `animate-pulse` : ``}`} />
                                        {status == "publishing"
                                            ? t('composer.publishingNotice', 'This post is being published — fields are read-only.')
                                            : t('composer.publishedNotice', 'This post was published — fields are read-only.')
                                        }
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-5 pb-8">
                                <ComposerForm
                                    posts={posts}
                                    postGroup={postGroup}
                                    activeSocialNetwork={activeSocialNetwork}
                                    isEditable={isEditable}
                                    onTextEdit={markDirty(onTextEdit)}
                                    onTitleEdit={markDirty(onTitleEdit)}
                                    onLinkEdit={markDirty(onLinkEdit)}
                                    onAssetNew={markDirty(onAssetNew)}
                                    onAssetDelete={markDirty(onAssetDelete)}
                                    onAssetEdit={markDirty(onAssetEdit)}
                                    onCoverEdit={markDirty(onCoverEdit)}
                                    onPreferencesEdit={markDirty(onPreferencesEdit)}
                                    onCommentsToCreateEdit={markDirty(onCommentsToCreateEdit)}
                                    onCreateNetworkPost={markDirty(onCreateNetworkPost)}
                                    onDeleteIndexPost={markDirty(onDeleteIndexPost)}
                                />
                            </div>
                        </div>

                        {/* Right: readonly live preview */}
                        <ComposerPreviewPane
                            className={mobilePane === "edit" ? `hidden lg:flex` : `flex`}
                            PreviewComponent={PreviewComponent}
                            posts={posts}
                            activeSocialNetwork={activeSocialNetwork}
                            postIndex={postIndex}
                            postGroup={postGroup}
                            onAssetDelete={onAssetDelete}
                            onAssetEdit={onAssetEdit}
                            onAssetNew={onAssetNew}
                            onTextEdit={onTextEdit}
                            onTitleEdit={onTitleEdit}
                            onLinkEdit={onLinkEdit}
                            onCreateNetworkPost={onCreateNetworkPost}
                            onDeleteNetworkPost={onDeleteNetworkPost}
                            onDeleteIndexPost={onDeleteIndexPost}
                            onPreferencesEdit={onPreferencesEdit}
                            onCoverEdit={onCoverEdit}
                            onCommentsToCreateEdit={onCommentsToCreateEdit}
                        />
                    </div>

                    {/* ───── Footer ───── */}
                    <div className="shrink-0 min-h-[64px] px-4 sm:px-6 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] sm:pb-3 flex items-center justify-between gap-3 border-t border-gray-200 dark:border-gray-700 bg-secondary-500/90 dark:bg-gray-800/90 backdrop-blur">
                        <div className="flex items-center gap-2 text-sm text-tertiary-600 dark:text-gray-300 min-w-0">
                            <span className="shrink-0">{scheduleSummary.icon}</span>
                            <span className="truncate">{scheduleSummary.text}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                size="sm"
                                variant="plain"
                                className="!rounded-xl !shrink-0 whitespace-nowrap hidden sm:flex"
                                onClick={() => requestClose()}
                            >
                                {t('composer.cancel', 'Cancel')}
                            </Button>
                            {showPostArticleButton && (
                                <div className="shrink-0 [&_.dropdown]:!w-auto">
                                    <PostArticleButton
                                        postGroup={postGroup}
                                        onSave={onSave}
                                        onPostNow={onPostNow}
                                        status={status}
                                        updateFieldValuePostGroup={updateFieldValuePostGroup}
                                        isLoading={isLoadingPostArticleButton}
                                        showSaveButton={showSaveButton}
                                        size="sm"
                                        placement="top-end"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </motion.div>

            <Dialog
                isOpen={confirmDiscardOpen}
                onClose={() => setConfirmDiscardOpen(false)}
                onRequestClose={() => setConfirmDiscardOpen(false)}
                portalClassName="!z-[100000000] relative"
                overlayClassName="flex items-center justify-center"
                contentClassName="!p-0 !rounded-2xl"
                width={400}
                closable={false}
            >
                <div className="p-5 flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 w-9 h-9 rounded-full bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center">
                            <HiOutlineExclamationTriangle className="text-lg text-amber-500" />
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <h6 className="text-base font-bold text-gray-900 dark:text-gray-100">
                                {t('composer.discardTitle', 'Discard changes?')}
                            </h6>
                            <p className="text-sm text-tertiary-500 dark:text-gray-400">
                                {t('composer.discardBody', 'Your unsaved edits will be lost.')}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            size="sm"
                            variant="plain"
                            className="!rounded-lg"
                            onClick={() => setConfirmDiscardOpen(false)}
                        >
                            {t('composer.discardCancel', 'Keep editing')}
                        </Button>
                        <Button
                            size="sm"
                            variant="solid"
                            color="red-600"
                            className="!rounded-lg"
                            onClick={() => {
                                setConfirmDiscardOpen(false);
                                onClose?.();
                            }}
                        >
                            {t('composer.discardConfirm', 'Discard')}
                        </Button>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default PostComposer;
